import { forwardRef } from 'react';
import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  ForwardedRef,
  ReactNode,
} from 'react';
import { Link } from 'react-router-dom';
import type { LinkProps } from 'react-router-dom';

/**
 * Three design-system variants, all per Figma:
 *
 *   primary — Play Now (node 231:543). White pill, Orbitron Medium 20px,
 *             soft cyan-tinted halo. State map:
 *               enabled   → bg-white + #352f42 text + 25px halo
 *               hover     → text fades to #afa9bd, inner white glow + 50px halo
 *               active    → bg lifts to #ebebeb, text inverts to white
 *               disabled  → bg-white/50, text @ 50% opacity
 *
 *   dark — Learn More / Gamerhood Playlist / Log Out (node 229:543).
 *          Purple-gradient pill, Arial 18px, magenta drop-shadow + inset
 *          lilac highlight.
 *               enabled   → from-#463e57 to-#31263d, white text
 *               hover     → adds an outer #ff0048 red glow on top of the base shadows
 *               active    → top stop darkens to #29213a, text shifts to #674d87
 *               disabled  → opacity 50%, text greys to #9f9f9f
 *
 *   destructive — Delete Account (Account page node 362:1168). Same pill
 *                 shape + shadow stack as `dark`, but with a lighter,
 *                 more saturated purple gradient so it visually separates
 *                 from "Log Out" as a stronger action.
 *               enabled   → from-#763bbe to-#9959e8, white text
 *               hover     → adds outer #ff0048 red glow
 *               active    → top stop darkens to #5b2a96, slight scale-down
 *               disabled  → opacity 50%, text greys to #d8c9ee
 *
 * Variants own their visual size — no `size` prop. Consumers pass
 * `fullWidth` (stretches to container) or override via `className`.
 */
export type ButtonVariant = 'primary' | 'dark' | 'destructive';

type CommonProps = {
  variant?: ButtonVariant;
  fullWidth?: boolean;
  className?: string;
  children: ReactNode;
};

type AsButton = CommonProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'children'> & {
    href?: undefined;
    to?: undefined;
  };

type AsAnchor = CommonProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'className' | 'children' | 'href'> & {
    href: string;
    to?: undefined;
  };

type AsLink = CommonProps &
  Omit<LinkProps, 'className' | 'children' | 'to'> & {
    to: LinkProps['to'];
    href?: undefined;
  };

export type ButtonProps = AsButton | AsAnchor | AsLink;

const BASE =
  'relative inline-flex select-none items-center justify-center whitespace-nowrap rounded-full ' +
  'px-[24px] py-[18px] transition duration-200 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-brand-deep';

// `hover:` / `active:` / `disabled:` Tailwind utilities replace the base
// utility they target — for box-shadow, that means the hover/disabled
// shadow strings include EVERY layer (Tailwind doesn't append). Keep all
// four shadow stacks in sync if you add another layer.
const VARIANTS: Record<ButtonVariant, string> = {
  primary: [
    'bg-white text-[#352f42]',
    "font-['Orbitron',sans-serif] font-medium uppercase tracking-[-0.02em]",
    'text-[clamp(16px,4vw,20px)] leading-[1.35]',
    'shadow-[0_0_25px_rgba(218,255,241,0.72)]',
    // Hover — text fades to lilac-grey, halo intensifies with an inner white glow.
    'hover:text-[#afa9bd] hover:shadow-[0_0_15px_rgba(255,255,255,1),0_0_50px_rgba(218,255,241,0.72)]',
    // Pressed — bg lifts to a pale grey, text inverts to white. Slight scale-down
    // for tactile feedback.
    'active:bg-[#ebebeb] active:text-white active:scale-[0.98]',
    // Disabled — translucent surface, dimmed text, no cursor affordance.
    'disabled:bg-white/50 disabled:text-[rgba(53,47,66,0.5)] disabled:shadow-[0_0_50px_rgba(218,255,241,0.72)] disabled:cursor-not-allowed disabled:hover:text-[rgba(53,47,66,0.5)]',
  ].join(' '),
  dark: [
    'bg-gradient-to-b from-[#463e57] to-[#31263d] text-white',
    "font-arial leading-[1.2] tracking-[-0.02em]",
    'text-[clamp(14px,3.5vw,18px)]',
    // Layered shadow: faint magenta outer glow + dropped offset shadow + inset lilac highlight.
    'shadow-[0_0_4px_#ff4479,-5px_5px_5px_rgba(0,0,0,0.25),inset_0_4px_4px_rgba(165,111,231,0.4)]',
    // Hover — adds a stronger red bloom on top of the base shadow stack.
    'hover:shadow-[0_0_15px_#ff0048,0_0_4px_#ff4479,-5px_5px_5px_rgba(0,0,0,0.25),inset_0_4px_4px_rgba(165,111,231,0.4)]',
    // Pressed — top gradient stop darkens, text shifts to a muted purple, button presses down.
    'active:from-[#29213a] active:text-[#674d87] active:scale-[0.98]',
    'disabled:opacity-50 disabled:text-[#9f9f9f] disabled:cursor-not-allowed',
  ].join(' '),
  destructive: [
    'bg-gradient-to-b from-[#763bbe] to-[#9959e8] text-white',
    "font-arial leading-[1.2] tracking-[-0.02em]",
    'text-[clamp(14px,3.5vw,18px)]',
    'shadow-[0_0_4px_#ff4479,-5px_5px_5px_rgba(0,0,0,0.25),inset_0_4px_4px_rgba(165,111,231,0.4)]',
    'hover:shadow-[0_0_15px_#ff0048,0_0_4px_#ff4479,-5px_5px_5px_rgba(0,0,0,0.25),inset_0_4px_4px_rgba(165,111,231,0.4)]',
    'active:from-[#5b2a96] active:scale-[0.98]',
    'disabled:opacity-50 disabled:text-[#d8c9ee] disabled:cursor-not-allowed',
  ].join(' '),
};

function buildClassName(
  variant: ButtonVariant,
  fullWidth: boolean,
  extra: string | undefined,
): string {
  return [BASE, VARIANTS[variant], fullWidth ? 'w-full' : '', extra ?? '']
    .filter(Boolean)
    .join(' ');
}

function isAnchorProps(p: ButtonProps): p is AsAnchor {
  return typeof (p as AsAnchor).href === 'string';
}

function isLinkProps(p: ButtonProps): p is AsLink {
  return (p as AsLink).to !== undefined;
}

export const Button = forwardRef(function Button(
  props: ButtonProps,
  ref: ForwardedRef<HTMLButtonElement> | ForwardedRef<HTMLAnchorElement>,
) {
  const { variant = 'primary', fullWidth = false, className, children, ...rest } = props;

  const cls = buildClassName(variant, fullWidth, className);

  if (isLinkProps(props)) {
    const { to, ...linkRest } = rest as Omit<AsLink, keyof CommonProps>;
    return (
      <Link
        ref={ref as ForwardedRef<HTMLAnchorElement>}
        to={to}
        className={cls}
        {...linkRest}
      >
        {children}
      </Link>
    );
  }

  if (isAnchorProps(props)) {
    return (
      <a
        ref={ref as ForwardedRef<HTMLAnchorElement>}
        className={cls}
        {...(rest as Omit<AsAnchor, keyof CommonProps>)}
      >
        {children}
      </a>
    );
  }

  const buttonRest = rest as Omit<AsButton, keyof CommonProps>;
  return (
    <button
      ref={ref as ForwardedRef<HTMLButtonElement>}
      type={buttonRest.type ?? 'button'}
      className={cls}
      {...buttonRest}
    >
      {children}
    </button>
  );
});
